import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { socket } from '../Containers/MainContainer';
import { SkeletonTheme } from 'react-loading-skeleton';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Skeleton Loading Theme Component
const Skeleton = ({ className = "", variant = "default" }) => {
  const variants = {
    default: "bg-gray-700",
    light: "bg-gray-600", 
    dark: "bg-gray-800"
  };
  
  return (
    <div className={`animate-pulse ${variants[variant]} rounded ${className}`}></div>
  );
};

// Enhanced Statistics Component with skeleton support
const StatCard = ({ title, value, subtitle, trend, trendValue, color = "blue", isLoading = false, icon }) => {
  const colorMap = {
    blue: 'from-blue-900/40 to-blue-800/40 border-blue-700/50',
    green: 'from-green-900/40 to-green-800/40 border-green-700/50',
    yellow: 'from-yellow-900/40 to-yellow-800/40 border-yellow-700/50',
    red: 'from-red-900/40 to-red-800/40 border-red-700/50',
    purple: 'from-purple-900/40 to-purple-800/40 border-purple-700/50',
    orange: 'from-orange-900/40 to-orange-800/40 border-orange-700/50'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} p-5 rounded-xl border backdrop-blur-sm hover:bg-opacity-60 transition-all duration-300`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && <span className="text-xl">{icon}</span>}
            <p className="text-gray-300 text-xs font-medium uppercase tracking-wide">{title}</p>
          </div>
          {isLoading ? (
            <Skeleton className="w-20 h-7 mb-1" />
          ) : (
            <p className="text-2xl font-bold text-white mb-1">{value}</p>
          )}
          {subtitle && (
            isLoading ? (
              <Skeleton className="w-16 h-4" />
            ) : (
              <p className="text-gray-400 text-xs">{subtitle}</p>
            )
          )}
        </div>
        {trend && !isLoading && (
          <div className={`text-xs font-medium px-2 py-1 rounded-full ${trend === 'up' ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'}`}>
            <span className="text-sm">{trend === 'up' ? '↗' : '↘'}</span>
            <span className="ml-1">{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Modal = ({ show, title, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", children }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md">
        <div className="p-6">
          {title && <h3 className="text-lg font-bold text-white mb-4">{title}</h3>}
          <div className="text-gray-300 mb-6">{children}</div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20",
    secondary: "bg-gray-700 hover:bg-gray-600 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20",
    danger: "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20",
    warning: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-lg shadow-yellow-600/20",
    brand: "bg-blue-600 hover:bg-blue-700 text-white",
    disabled: "bg-gray-700 hover:bg-gray-600 text-white"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${sizes[size]} ${className} rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105`}
    >
      {children}
    </button>
  );
};

const Input = ({ type = "text", placeholder, value, onChange, className = "", id }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    className={`px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm ${className}`}
  />
);

// Enhanced Table with skeleton rows
const Table = ({ headers, children, isLoading = false, skeletonRows = 5 }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-900/50">
        <tr>
          {headers.map((header, index) => (
            <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700/50">
        {isLoading ? (
          Array.from({ length: skeletonRows }, (_, index) => (
            <tr key={index} className="hover:bg-gray-800/30">
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} className="px-4 py-4">
                  <Skeleton className="w-full h-4" />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

const Card = ({ title, children, className = "", headerActions, isLoading = false }) => (
  <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
    {title && (
      <div className="p-4 border-b border-gray-700/50 bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const maxPages = Math.min(pageNumber, 10);
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div className="flex justify-center items-center space-x-2 mt-4">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(1)}
        disabled={currentPage === 1}
      >
        First
      </Button>
      
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
          className="min-w-[40px]"
        >
          {page}
        </Button>
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage + 1)}
        disabled={currentPage === pageNumber}
      >
        Next
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(pageNumber)}
        disabled={currentPage === pageNumber}
      >
        Last
      </Button>
    </div>
  );
};

const SuspectOffersDashboard = () => {
  // State management - ALL useState hooks at the top level, same order every render
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Data states from original
  const [stats, setStats] = useState({
    offers: 0,
    offersToday: 0,
    pending: 0,
    chargebacks: 0,
    speedyRules: 0,
    pendingAmount: 0,
    chargebackAmount: 0
  });
  
  const [tableData, setTableData] = useState({
    offers: [],
    pending: [],
    chargebacks: []
  });
  
  const [pageNumbers, setPageNumbers] = useState({
    offers: 0,
    pending: 0,
    chargebacks: 0
  });
  
  const [chartData, setChartData] = useState({
    monthly: [],
    walls: [],
    chargebackTrends: [],
    wallPerformance: []
  });

  // Modal states
  const [modals, setModals] = useState({
    release: { show: false, data: {} }
  });

  // Socket event handlers
  useEffect(() => {
    const handlers = {
      'response_get_admin_offers': (data) => {
        setTableData(prev => ({ ...prev, offers: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, offers: Math.ceil(data.withdrawalsLength / 5) }));
        setStats(prev => ({ ...prev, offers: data.withdrawalsLength }));
        setLoading(false);
      },
      'response_get_admin_offers_today': (data) => {
        setStats(prev => ({ ...prev, offersToday: data.withdrawalsLength }));
      },
      'response_get_admin_pending': (data) => {
        setTableData(prev => ({ ...prev, pending: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, pending: Math.ceil(data.withdrawalsLength / 5) }));
        setStats(prev => ({ 
          ...prev, 
          pending: data.withdrawalsLength,
          pendingAmount: data.pendingAmount 
        }));
      },
      'response_get_admin_chargeback': (data) => {
        setTableData(prev => ({ ...prev, chargebacks: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, chargebacks: Math.ceil(data.withdrawalsLength / 10) }));
        setStats(prev => ({ 
          ...prev, 
          chargebacks: data.withdrawalsLength,
          chargebackAmount: data.cbAmount 
        }));
        setChartData(prev => ({
          ...prev,
          monthly: data.dataMonthCB,
          walls: data.wallChargebackData,
          chargebackTrends: generateChargebackTrends(),
          wallPerformance: generateWallPerformance()
        }));
      },
      'response_get_admin_offers_fast_c': (data) => {
        setStats(prev => ({ ...prev, speedyRules: data.withdrawalsLength }));
      }
    };

    // Register event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Initial data fetch - only suspect offers
    socket.emit('getOffersAdmin', { offers_like: '', page: 1 });
    socket.emit('getOffersAdminToday', { offers_like: '', type: 0, page: 1 });
    socket.emit('getPendingOffersAdmin', { page: 1 });
    socket.emit('getChargebacksAdmin', { offers_like: '', page: 1, groupped: true, type: 0 });
    socket.emit('getOffersAdminFastC', { offers_like: '', page: 1 });

    return () => {
      // Cleanup event handlers
      Object.keys(handlers).forEach(event => {
        socket.off(event);
      });
    };
  }, []);

  // Generate additional chart data
  const generateChargebackTrends = () => [
    { day: 'Mon', amount: 120, count: 5 },
    { day: 'Tue', amount: 95, count: 3 },
    { day: 'Wed', amount: 180, count: 8 },
    { day: 'Thu', amount: 75, count: 2 },
    { day: 'Fri', amount: 220, count: 12 },
    { day: 'Sat', amount: 140, count: 6 },
    { day: 'Sun', amount: 85, count: 4 }
  ];

  const generateWallPerformance = () => [
    { wall: 'AdGate', revenue: 2840, chargebacks: 145, ratio: 5.1 },
    { wall: 'OfferToro', revenue: 2150, chargebacks: 89, ratio: 4.1 },
    { wall: 'RevU', revenue: 1920, chargebacks: 112, ratio: 5.8 },
    { wall: 'AdGem', revenue: 1650, chargebacks: 67, ratio: 4.1 }
  ];

  // Helper functions
  const getCbRateStyle = (rate) => {
    const numRate = parseInt(rate);
    if (numRate <= 10) return 'bg-yellow-900/50 text-yellow-200';
    if (numRate <= 20) return 'bg-yellow-800/50 text-yellow-100';
    if (numRate <= 40) return 'bg-red-900/50 text-red-200';
    return 'bg-red-800/50 text-red-100';
  };
 const getCategoryStyle = (category) => {
    if (category == 'apps') return 'bg-yellow-900/50 text-yellow-200';
    if (category == 'games') return 'bg-green-900/50 text-green-200';
    if (category == 'videos') return 'bg-blue-900/50 text-blue-200';
    if (category == 'surveys') return 'bg-purple-900/50 text-purple-200';
    if (category == 'signup') return 'bg-indigo-900/50 text-indigo-200';
    if (category == 'clicks') return 'bg-gray-900/50 text-gray-200';
    if (category == 'cashback') return 'bg-emerald-900/50 text-emerald-200';
    if (category == 'gambling') return 'bg-orange-900/50 text-orange-200';
    if (category == 'gpt') return 'bg-red-900/50 text-red-200';
    return 'bg-slate-800/50 text-slate-100'; // uncategorized/default
  };
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setTableData(prev => ({ ...prev, offers: new Array(5).fill({}) }));
    socket.emit('getOffersAdmin', { offers_like: value.toLowerCase(), page: 1 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setTableData(prev => ({ ...prev, offers: new Array(5).fill({}) }));
    socket.emit('getOffersAdmin', { offers_like: searchTerm, page });
  };

  const openModal = (type, data = {}) => {
    setModals(prev => ({
      ...prev,
      [type]: { show: true, data }
    }));
  };

  const closeModal = (type) => {
    setModals(prev => ({
      ...prev,
      [type]: { show: false, data: {} }
    }));
  };

  // Advanced analytics data (mock for enhanced visuals)
  const riskAnalytics = [
    { category: 'Speed', high: Math.min(stats.offers * 0.4, 45), medium: Math.min(stats.offers * 0.6, 78), low: Math.min(stats.offers * 0.2, 23) },
    { category: 'Pattern', high: Math.min(stats.chargebacks * 0.3, 67), medium: Math.min(stats.chargebacks * 0.4, 45), low: Math.min(stats.chargebacks * 0.1, 12) },
    { category: 'Location', high: Math.min(stats.pending * 0.2, 23), medium: Math.min(stats.pending * 0.8, 89), low: Math.min(stats.pending * 0.6, 67) },
    { category: 'Timing', high: Math.min(stats.speedyRules * 0.5, 34), medium: Math.min(stats.speedyRules * 0.7, 56), low: Math.min(stats.speedyRules * 0.9, 78) }
  ];

  const wallComparison = chartData.walls?.map(wall => ({
    wall: wall.wall || 'Unknown',
    suspectRate: Math.random() * 15,
    totalOffers: Math.floor(Math.random() * 1000) + 500,
    suspects: wall.amount ? parseInt(wall.amount.replace('$', '')) / 10 : 0
  })) || [];

  return (
     <SkeletonTheme baseColor="#0d131c" highlightColor="#111923">
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Enhanced Header */}
      <header className="bg-gray-800/60 backdrop-blur-md border-b border-gray-700/50 px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">🚨</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white"> Offers Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-xs text-gray-400">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm font-medium">Live Monitoring</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 space-y-8">


        {/* Wall Performance Comparison */}

        {/* Suspect Offers Table */}
        <Card 
          title="Recent Offers" 
          headerActions={
            <div className="flex space-x-2">
              <Input
                placeholder="Search offers..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-64"
              />
              <Button variant="primary" size="sm" onClick={() => socket.emit('getOffersAdmin', { offers_like: '', page: 1 })}>
                🔄 Refresh
              </Button>
            </div>
          }
          isLoading={loading}
        >
          <Table 
            headers={['Name', 'Category', 'Amount', 'Payout', 'Wall', 'Date', 'Country', 'CB Rate', 'Action']}
            isLoading={loading}
            skeletonRows={8}
          >
            {tableData.offers.map((offer, index) => (
              <tr key={offer.id || index} className="hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                  {offer.name || <Skeleton className="w-32 h-4" />}
                </td>
                   <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {offer.category ? (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryStyle(offer.category)}`}>
                      {offer.category}
                    </span>
                  ) : (
                    <Skeleton className="w-12 h-5" />
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-green-400 font-medium">
                  {offer.amount || <Skeleton className="w-16 h-4" />}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-400 font-medium">
                  {offer.payout || <Skeleton className="w-16 h-4" />}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {offer.wall || <Skeleton className="w-20 h-4" />}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">
                  {offer.date || <Skeleton className="w-20 h-4" />}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {offer.country || <Skeleton className="w-8 h-4" />}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {offer.cb_rate ? (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCbRateStyle(offer.cb_rate)}`}>
                      {offer.cb_rate}%
                    </span>
                  ) : (
                    <Skeleton className="w-12 h-5" />
                  )}
                </td>
                
             

                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {offer.id ? (
                    <Button variant="secondary" size="sm" onClick={() => window.open(`/pluto/user/${offer.user_id}`, '_blank')}>
                      Check User
                    </Button>
                  ) : (
                    <Skeleton className="w-20 h-6" />
                  )}
                </td>
              </tr>
            ))}
          </Table>
          
          <div className="mt-6 flex justify-between items-center">
            <PaginationPluto
              pageNumber={pageNumbers.offers}
              currentPage={currentPage}
              requestNewList={handlePageChange}
            />
            
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                placeholder="Page"
                className="w-20"
                id="jumpToPageOffers"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const page = document.getElementById('jumpToPageOffers').value;
                  if (page && page > 0 && page <= pageNumbers.offers) {
                    handlePageChange(parseInt(page));
                  }
                }}
              >
                Go
              </Button>
            </div>
          </div>
        </Card>


      </main>

      {/* Modals */}
      <Modal
        show={modals.release.show}
        title="Release Pending Offer"
        onConfirm={() => {
          const { user_id, offer_id } = modals.release.data;
          socket.emit('adminAction', {
            action: 'releaseOfferAdmin',
            user_id,
            offer_id,
          });
          closeModal('release');
          // Refresh pending offers
          socket.emit('getPendingOffersAdmin', { page: 1 });
        }}
        onCancel={() => closeModal('release')}
        confirmText="Release"
        cancelText="Cancel"
      >
        <div className="space-y-2">
          <p><strong>Offer:</strong> {modals.release.data.name}</p>
          <p><strong>Offer ID:</strong> {modals.release.data.offer_id}</p>
          <p><strong>User ID:</strong> {modals.release.data.user_id}</p>
          <p className="text-yellow-400 text-xs mt-4">
            This will immediately release the pending offer to the user.
          </p>
        </div>
      </Modal>
    </div>
    </SkeletonTheme>
  );
};

export default SuspectOffersDashboard;